import * as React from "react"
import { ChevronLeft, ChevronRight } from "lucide-react"

import { cn } from "../../lib/utils"
import styles from "./pagination.module.css"

const Pagination = ({ className, ...props }) => (
  <nav
    role="navigation"
    aria-label="pagination"
    className={cn(styles.root, className)}
    {...props}
  />
)
Pagination.displayName = "Pagination"

const PaginationContent = React.forwardRef(({ className, ...props }, ref) => (
  <ul
    ref={ref}
    className={cn(styles.content, className)}
    {...props}
  />
))
PaginationContent.displayName = "PaginationContent"

const PaginationItem = React.forwardRef(({ className, ...props }, ref) => (
  <li ref={ref} className={cn(styles.item, className)} {...props} />
))
PaginationItem.displayName = "PaginationItem"

const PaginationLink = ({ className, isActive, ...props }) => (
  <button
    type="button"
    aria-current={isActive ? "page" : undefined}
    className={cn(styles.link, isActive && styles.linkActive, className)}
    {...props}
  />
)
PaginationLink.displayName = "PaginationLink"

const PaginationPrevious = ({ className, ...props }) => (
  <button
    type="button"
    aria-label="Go to previous page"
    className={cn(styles.prevNext, className)}
    {...props}
  >
    <ChevronLeft size={13} />
    <span>Prev</span>
  </button>
)
PaginationPrevious.displayName = "PaginationPrevious"

const PaginationNext = ({ className, ...props }) => (
  <button
    type="button"
    aria-label="Go to next page"
    className={cn(styles.prevNext, className)}
    {...props}
  >
    <span>Next</span>
    <ChevronRight size={13} />
  </button>
)
PaginationNext.displayName = "PaginationNext"

const PaginationEllipsis = ({ className, ...props }) => (
  <button
    type="button"
    className={cn(styles.ellipsis, className)}
    {...props}
  >
   ...
  </button>
)
PaginationEllipsis.displayName = "PaginationEllipsis"

// Up to three page numbers around the active page, plus an ellipsis that jumps forward
function getVisiblePages(totalPages, activePage) {
  if (totalPages <= 3) {
    return {
      pages: Array.from({ length: totalPages }, (_, i) => i + 1),
      showEllipsis: false,
      ellipsisTarget: null,
    };
  }

  const startPage = Math.min(Math.max(activePage - 1, 1), totalPages - 2);
  const pages = Array.from({ length: 3 }, (_, i) => startPage + i);

  return {
    pages,
    showEllipsis: pages.at(-1) < totalPages,
    ellipsisTarget: pages.at(-1) + 1,
  };
}

export {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  getVisiblePages,
}
